import React from 'react';
import { Mic, Check, DollarSignIcon, WifiIcon, SparklesIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { useLanguage } from '../hooks/useLanguage';
export function HeroSection() {
  const {
    t
  } = useLanguage();
  return <section className="relative min-h-screen flex items-center justify-center py-20 px-4 overflow-hidden bg-gradient-to-br from-[#134E4A] via-[#0D9488] to-[#0F766E]" data-id="element-83">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0" data-id="element-84">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-teal-400/10 rounded-full blur-[120px] opacity-50" data-id="element-85"></div>
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-brand-amber/10 rounded-full blur-[100px] opacity-40" data-id="element-86"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1478737270239-2f02b77ac6d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center opacity-5 mix-blend-overlay" data-id="element-87"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-88">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-brand-amber font-bold uppercase tracking-wider text-sm mb-8 animate-fade-in-up" data-id="element-89">
          <Mic className="w-4 h-4" data-id="element-90" />
          <span data-id="element-91">{t('hero', 'badge')}</span>
        </div>

        <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-white mb-6 leading-tight uppercase tracking-tight drop-shadow-2xl animate-fade-in-up [animation-delay:200ms]" data-id="element-92">
          {t('hero', 'title1')}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-amber to-pink-300" data-id="element-93">
            {t('hero', 'title2')}
          </span>
        </h1>

        <p className="text-xl md:text-2xl text-teal-100 mb-6 max-w-2xl mx-auto font-light leading-relaxed animate-fade-in-up [animation-delay:400ms]" data-id="element-94">
          {t('hero', 'subtitle')}
        </p>

        {/* Highlight Banner */}
        <div className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-amber/15 border border-brand-amber/30 text-white mb-10 animate-fade-in-up [animation-delay:450ms]" data-id="element-95">
          <SparklesIcon className="w-5 h-5 text-brand-amber shrink-0" data-id="element-96" />
          <span className="text-sm md:text-base font-semibold" data-id="element-97">
            {t('hero', 'highlight1')}
            <span className="text-brand-amber" data-id="element-98">{t('hero', 'highlight2')}</span>
            {t('hero', 'highlight3')}
          </span>
        </div>

        {/* Product Image */}
        <div className="mb-10 animate-fade-in-up [animation-delay:500ms]" data-id="element-99">
          <img src={t('hero', 'productImage')} alt="Guía para entrar al mundo del doblaje de voz" className="mx-auto max-w-sm md:max-w-md lg:max-w-lg w-full h-auto rounded-xl shadow-2xl shadow-black/40 border border-white/10" data-id="element-100" />
        </div>

        <div className="flex flex-col items-center gap-4 animate-fade-in-up [animation-delay:600ms]" data-id="element-101">
          <a href={t('hero', 'ctaLink')} target="_blank" rel="noopener noreferrer" data-id="element-102">
            <Button variant="primary" size="xl" glow withIcon data-id="element-103">
              {t('hero', 'cta')}
            </Button>
          </a>

          <div className="flex flex-wrap justify-center gap-4 text-sm text-teal-200 mt-2" data-id="element-104">
            <span className="flex items-center gap-1" data-id="element-105">
              <Check className="w-4 h-4 text-brand-amber" data-id="element-106" />{' '}
              {t('hero', 'check1')}
            </span>
            <span className="flex items-center gap-1" data-id="element-107">
              <Check className="w-4 h-4 text-brand-amber" data-id="element-108" />{' '}
              {t('hero', 'check2')}
            </span>
            <span className="flex items-center gap-1" data-id="element-109">
              <Check className="w-4 h-4 text-brand-amber" data-id="element-110" />{' '}
              {t('hero', 'check3')}
            </span>
          </div>

          {/* Persuasive Sales Badges */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 w-full max-w-2xl" data-id="element-111">
            {/* Badge 1 — Earnings */}
            <div className="flex items-center gap-4 bg-gradient-to-r from-brand-amber to-brand-gold rounded-2xl px-6 py-5 shadow-xl shadow-brand-amber/30 border-2 border-pink-300/50" data-id="element-112">
              <div className="shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center" data-id="element-113">
                <DollarSignIcon className="w-7 h-7 text-white" data-id="element-114" />
              </div>
              <div className="text-left" data-id="element-115">
                <p className="text-white font-heading font-bold text-lg leading-tight uppercase tracking-wide" data-id="element-116">
                  {t('hero', 'badge1Title')}
                </p>
                <p className="text-pink-900 font-bold text-sm" data-id="element-117">
                  {t('hero', 'badge1Sub')}
                </p>
              </div>
            </div>

            {/* Badge 2 — Remote Work */}
            <div className="flex items-center gap-4 bg-gradient-to-r from-white/15 to-white/10 backdrop-blur-sm rounded-2xl px-6 py-5 shadow-xl border-2 border-white/30" data-id="element-118">
              <div className="shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center" data-id="element-119">
                <WifiIcon className="w-7 h-7 text-white" data-id="element-120" />
              </div>
              <div className="text-left" data-id="element-121">
                <p className="text-white font-heading font-bold text-lg leading-tight uppercase tracking-wide" data-id="element-122">
                  {t('hero', 'badge2Title')}
                </p>
                <p className="text-teal-200 font-semibold text-sm" data-id="element-123">
                  {t('hero', 'badge2Sub')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}